/** Formatting helpers shared by every screen, so dates never render inconsistently. */

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** A calendar date (`2025-08-01`) or an ISO timestamp, rendered as a local date. Never throws. */
export function formatDate(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  // A calendar date is built in local time on purpose: `new Date('2025-08-01')`
  // parses as UTC midnight and renders as the previous day west of Greenwich.
  // Anything else (an ISO timestamp such as `publishedAt`) is an instant, shown
  // as the viewer's local date.
  const calendar = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date =
    calendar !== null
      ? new Date(Number(calendar[1]), Number(calendar[2]) - 1, Number(calendar[3]))
      : new Date(value);
  // An unreadable value is shown as it came rather than thrown: Intl throws a
  // RangeError on an invalid date, and one bad field must not blank a page.
  return Number.isNaN(date.getTime()) ? value : DATE_FORMAT.format(date);
}

/** An instant, rendered in the viewer's own timezone. */
export function formatDateTime(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_TIME_FORMAT.format(date);
}

/** Turns an enum constant into something readable: `CO_CURRICULAR` → `Co curricular`. */
export function humanise(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const lower = value.replaceAll('_', ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function orDash(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  return String(value);
}

/**
 * Money, formatted for reading.
 *
 * <p>Grouped, always two decimal places, and the currency code beside it rather
 * than a symbol: a school in Karachi and one in Lagos both want their own code,
 * and PKR/NGN have no universally recognised glyph. The backend sends the code
 * with every financial response for exactly this reason.
 *
 * Amounts arrive as JSON numbers from BigDecimal columns. That is safe for
 * display — the value has already been computed and rounded server-side — but it
 * is never used to compute anything here. Every total on screen is one the
 * backend produced.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currencyCode = 'PKR',
): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(value)) {
    return '—';
  }
  return `${currencyCode} ${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** The number alone, grouped and to two places. For table columns with a currency header. */
export function formatAmount(amount: number | string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }
  const value = typeof amount === 'string' ? Number(amount) : amount;
  return Number.isNaN(value)
    ? '—'
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A percentage, or an em dash when the rate does not exist rather than being zero. */
export function formatRate(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '—' : `${rate.toFixed(1)}%`;
}
